import type { iBrand } from "./brand-model";
import {
  resolveOrganizationEmailConfig,
  resolveQuoteEmailDeliveryMethod,
} from "./quote-email-config.helpers";

export const PLATFORM_QUOTE_EMAIL_DOMAIN = "mail.holidayquotes.app";
export const PLATFORM_BOOKING_EMAIL_DOMAIN = "bookings.tadesk.com";

export function getOrganizationSlug(
  organizationDomain?: string | null
): string {
  return (organizationDomain ?? "")
    .trim()
    .toLowerCase()
    .replace(/\.tamaster$/i, "");
}

export function isPlatformEmailDefaultsEnabled(): boolean {
  return true;
}

export interface ResolvedBookingEmailDisplay {
  source: "custom" | "platform";
  domain: string;
  fromEmail: string;
  replyToEmail: string;
  isReady: boolean;
}

export function resolveBookingEmailDisplay(
  brand: Pick<iBrand, "resendEmailConfig" | "name"> | null | undefined,
  bookingId: string
): ResolvedBookingEmailDisplay {
  const custom = brand?.resendEmailConfig;
  if (custom?.domain?.trim() && custom.isVerified) {
    const domain = custom.domain.trim().toLowerCase();
    return {
      source: "custom",
      domain,
      fromEmail: `bookings@${domain}`,
      replyToEmail: `booking-${bookingId}@${domain}`,
      isReady: true,
    };
  }

  if (!isPlatformEmailDefaultsEnabled()) {
    return {
      source: "platform",
      domain: "",
      fromEmail: "",
      replyToEmail: "",
      isReady: false,
    };
  }

  const domain = PLATFORM_BOOKING_EMAIL_DOMAIN;
  return {
    source: "platform",
    domain,
    fromEmail: `bookings@${domain}`,
    replyToEmail: `booking-${bookingId}@${domain}`,
    isReady: true,
  };
}

export function hasCustomBookingEmailDomain(
  brand: Pick<iBrand, "resendEmailConfig"> | null | undefined
): boolean {
  return Boolean(
    brand?.resendEmailConfig?.isVerified &&
      brand.resendEmailConfig.domain?.trim()
  );
}

function getEmailHost(email: string): string {
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.lastIndexOf("@");
  if (at <= 0 || at === trimmed.length - 1) {
    return "";
  }
  return trimmed.slice(at + 1);
}

/** True when a non-platform custom quote delivery path is fully configured. */
export function isCustomQuoteEmailConfigured(
  brand: Pick<iBrand, "emailConfig" | "resendEmailConfig"> | null | undefined
): boolean {
  if (!brand) return false;

  const emailConfig = resolveOrganizationEmailConfig(brand.emailConfig);
  const method = emailConfig.deliveryMethod;

  if (method === "CUSTOM_SMTP") {
    return Boolean(
      emailConfig.server?.trim() &&
        emailConfig.username?.trim() &&
        emailConfig.password &&
        emailConfig.port != null &&
        emailConfig.port > 0
    );
  }

  if (method === "BOOKING_EMAIL_DOMAIN") {
    return hasCustomBookingEmailDomain(brand);
  }

  if (method === "CUSTOM_EMAIL_DOMAIN") {
    const quoteDomain =
      emailConfig.quoteResendEmailConfig ??
      brand.emailConfig?.quoteResendEmailConfig;

    if (!quoteDomain?.isVerified || !quoteDomain.domain?.trim()) {
      return false;
    }

    const senderName = emailConfig.senderName?.trim();
    const senderEmail = emailConfig.senderEmail?.trim();
    if (!senderName || !senderEmail) {
      return false;
    }

    return (
      getEmailHost(senderEmail) === quoteDomain.domain.trim().toLowerCase()
    );
  }

  return false;
}

export function buildPlatformQuoteFromEmail(orgSlug: string): string {
  return `${orgSlug}@${PLATFORM_QUOTE_EMAIL_DOMAIN}`;
}

export function buildPlatformQuoteFromDisplay(
  orgSlug: string,
  brandName?: string
): string {
  const email = buildPlatformQuoteFromEmail(orgSlug);
  const name = brandName?.trim() || "Quotes";
  return `${name} <${email}>`;
}

export function resolveQuoteEmailFromDisplay(
  brand: Pick<iBrand, "emailConfig" | "resendEmailConfig" | "name"> | null | undefined,
  organizationDomain?: string | null
): { source: "custom" | "platform"; fromDisplay: string } | null {
  if (!brand) return null;

  const emailConfig = resolveOrganizationEmailConfig(brand.emailConfig);
  const method = resolveQuoteEmailDeliveryMethod(emailConfig);
  const brandName = brand.name || "Quotes";

  if (method === "CUSTOM_SMTP" && isCustomQuoteEmailConfigured(brand)) {
    const fromEmail =
      emailConfig.senderEmail?.trim() ||
      emailConfig.email?.trim() ||
      emailConfig.username?.trim();
    if (!fromEmail) return null;
    const senderName = emailConfig.senderName?.trim() || brandName;
    return {
      source: "custom",
      fromDisplay: senderName ? `${senderName} <${fromEmail}>` : fromEmail,
    };
  }

  if (method === "BOOKING_EMAIL_DOMAIN" && hasCustomBookingEmailDomain(brand)) {
    const domain = brand.resendEmailConfig!.domain!.trim().toLowerCase();
    const senderName = emailConfig.senderName?.trim() || brandName;
    return {
      source: "custom",
      fromDisplay: `${senderName} <quotes@${domain}>`,
    };
  }

  if (method === "CUSTOM_EMAIL_DOMAIN" && isCustomQuoteEmailConfigured(brand)) {
    const senderName = emailConfig.senderName?.trim() || brandName;
    const senderEmail = emailConfig.senderEmail!.trim();
    return {
      source: "custom",
      fromDisplay: `${senderName} <${senderEmail}>`,
    };
  }

  const orgSlug = getOrganizationSlug(organizationDomain);
  if (!orgSlug || !isPlatformEmailDefaultsEnabled()) {
    return null;
  }

  return {
    source: "platform",
    fromDisplay: buildPlatformQuoteFromDisplay(orgSlug, brandName),
  };
}
